import { AnalyticsFactory } from '@gamiphy/analytics';
import { SessionHitTypes } from '@gamiphy/bl/analytics';
import { DiscountDto, PopulatedDiscountCode } from '@coretava/api/core-service';
import { SliderContainer } from '@gamiphy/loyalty-station-desktop/components/redeem/slider';
import { DiscountStore } from '@gamiphy/loyalty-station-desktop/store/reward/discount';
import { SdkStore } from '@gamiphy/loyalty-station-desktop/store/sdk';
import { Pages, Tabs, UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { Trans, useTranslation } from '@gamiphy/react-i18n';
import { useOnCopy } from '@gamiphy/utils/react';
import CheckIcon from '@mui/icons-material/Check';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import {
  Alert, Box, Button, Grid, Typography,
} from '@mui/material';
import { observer } from 'mobx-react';
import React from 'react';

const Redeem = observer(() => {
  const [showApplicationError, setShowApplicationError] = React.useState(false);
  const [redeemedDiscountCode, setRedeemedDiscountCode] = React.useState<DiscountDto>();
  const [triedAutoApply, setTriedAutoApply] = React.useState(false);
  const { t } = useTranslation();
  const {
    copied,
    onCopy,
  } = useOnCopy(redeemedDiscountCode?.code);

  const applied = React.useMemo(
    () => redeemedDiscountCode && SdkStore.cart?.data?.discountCodes.includes(redeemedDiscountCode.code),
    [
      SdkStore.cart?.data,
      redeemedDiscountCode,
    ],
  );

  const onRedeem = React.useCallback(async (discountCode: PopulatedDiscountCode) => {
    setRedeemedDiscountCode(discountCode.eDiscount);
    if (DiscountStore.isAutoApply && discountCode.eDiscount) {
      try {
        const orderFormId = await DiscountStore.applyDiscountCode(discountCode.eDiscount.code);
        await AnalyticsFactory.collect({
          type: SessionHitTypes.event,
          event: {
            category: 'AutoApply',
            action: 'AutoApply.clicked',
            attributes: {
              pointsAmount: Number(discountCode.points.gems)
                .toString(),
              pointsValue: Number(discountCode.eDiscount.value)
                .toString(),
              orderFormId,
            },
          },
        });
      } catch {
        setShowApplicationError(true);
      } finally {
        setTriedAutoApply(true);
      }
    } else {
      UiStore.setPage(Pages.redeemHistory);
    }
  }, [DiscountStore.isAutoApply]);

  const handleGetPoints = React.useCallback(() => {
    UiStore.setPage(Pages.home);
    UiStore.setTab(Tabs.Missions);
  }, []);

  const minimumOrderValueAlert = React.useMemo(() => {
    if (DiscountStore.appDiscount?.minimumOrderValue) {
      if ((SdkStore.cart?.data?.total ?? 0) >= DiscountStore.appDiscount.minimumOrderValue) {
        return (
          <Alert severity="info" sx={{ '& :first-letter': { textTransform: 'uppercase' } }}>
            <Trans
              i18nKey="redeemPage.minOrderValue.info"
              defaults="{{, discountType}} can be applied to orders with total value greater than or equal to {{minOrderValue, currency}}"
              values={{
                minOrderValue: DiscountStore.appDiscount.minimumOrderValue,
              }}
            />
          </Alert>
        );
      }
      return (
        <Alert severity="warning">
          <Trans
            i18nKey="redeemPage.minOrderValue.warning"
            defaults="Your {{, discountType}} will be applied to orders over {{minOrderValue, currency}}. It will be shown then under the payment section on checkout page."
            values={{
              minOrderValue: DiscountStore.appDiscount.minimumOrderValue ?? 1000,
            }}
          />
        </Alert>
      );
    }
    return null;
  }, [DiscountStore.appDiscount, SdkStore.cart?.data]);

  const content = React.useMemo(() => {
    switch (true) {
      case DiscountStore.inactive:
        return (
          <Alert severity="warning">
            <Trans
              i18nKey="redeemPage.inactiveWarning"
              defaults="Points redemption is temporarily inactive. It will be reactivated shortly"
            />
          </Alert>
        );
      case triedAutoApply && !applied:
        return (
          <Box display="flex" flexDirection="column" gap={2}>
            {minimumOrderValueAlert}
            <Typography sx={{ fontSize: '1.5rem' }} textAlign="center">
              {t('redeem.discountCodeNotApplied.message', 'Kindly manually enter the {{, discountType}} code during checkout to maximize your savings.')}
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              onClick={onCopy}
              endIcon={copied ? <CheckIcon fontSize="large" /> : <FileCopyIcon fontSize="large" />}
            >
              <Typography sx={{ fontWeight: 500 }}>
                {redeemedDiscountCode?.code}
              </Typography>
            </Button>
          </Box>
        );
      case triedAutoApply && applied:
        return (
          <Grid
            container
            direction="column"
            justifyContent="flex-end"
            spacing={2}
          >
            <Grid item>
              <Typography sx={{ fontSize: '1.5rem' }} textAlign="center">
                <Trans
                  i18nKey="redeem.discountCodeApplied.message"
                  defaults="<strong>Congratulations 🎉</strong><br/> Your gift card has been applied to your order. You can check it under the payment section on the checkout page"
                />
              </Typography>
            </Grid>
            <Grid item>
              {minimumOrderValueAlert}
            </Grid>
            <Grid item>
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                onClick={handleGetPoints}
              >
                <Trans
                  i18nKey="wallet.cta.getMore"
                  defaults="Get more {{, gems}}"
                  values={{}}
                />
              </Button>
            </Grid>
          </Grid>
        );
      default:
        return (
          <SliderContainer showApplicationError={showApplicationError} onRedeem={onRedeem} />
        );
    }
  }, [
    DiscountStore.inactive,
    minimumOrderValueAlert,
    showApplicationError,
    redeemedDiscountCode,
    applied,
    triedAutoApply,
    copied,
  ]);

  return (
    <Box>
      <Box
        p={2}
        pt={0}
        maxHeight={339}
        overflow="auto"
        sx={{
          '&::-webkit-scrollbar': {
            width: '5px',
          },
          '&::-webkit-scrollbar-track': {
            margin: 1,
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#00000066',
            borderRadius: '50px',
          },
        }}
      >
        {content}
      </Box>
    </Box>
  );
});

export default Redeem;
